import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type MenuStyle = 'colored' | 'bw';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  menuStyle: MenuStyle;
  setMenuStyle: (style: MenuStyle) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const MENU_STYLE_KEY = 'menuStyle';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme') as 'light' | 'dark' | null;
      if (saved === 'light' || saved === 'dark') return saved;
    }
    return 'light';
  });

  const [menuStyle, setMenuStyleState] = useState<MenuStyle>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(MENU_STYLE_KEY) as MenuStyle | null;
      if (saved === 'colored' || saved === 'bw') return saved;
    }
    return 'colored';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const setMenuStyle = (style: MenuStyle) => {
    setMenuStyleState(style);
    localStorage.setItem(MENU_STYLE_KEY, style);
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    menuStyle,
    setMenuStyle,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

